import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { useLocation } from "react-router-dom";
import T from "utils/constants/T";
import {
  fetchPayrollSettingsTabData,
  syncDeputyPayroll,
  syncMYOBPayroll,
  syncPayroll,
} from "store/reducers/companyPayrollSettings";
import { get } from "lodash";
import authApi from "interceptor/authInterceptor";
import MainCard from "components/MainCard";
import ViewPayrollSettings from "./ViewPayrollSettings";
import ViewPayrollSettingsTimeSheet from "./ViewPayrollSettingsTimeSheet";
import ViewPayrollSettingsMYOB from "./ViewPayrollSettingsMYOB";
import ViewPayrollSettingsMYOBBusiness from "./ViewPayrollSettingsMYOBBusiness";
import ViewPayrollSettingsTimeSitePro from "./ViewPayrollSettingsTimeSitePro";
import EditPayrollSettingsTimeSitePro from "./EditPayrollSettingsTimeSitePro";
import ViewPayrollSettingsBtn from "./ViewPayrollSettingsBtn";
import ViewPayrollSettingsReckon from "./ViewPayrollSettingsReckon";
import ViewPayrollSettingsXero from "./ViewPayrollSettingsXero";
import toast from "utils/ToastNotistack";
import {
  processReckonAccessToken,
  storeReckonCompanyDetails,
} from "store/reducers/reckon";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";

const ViewPayrollTab = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { selectedCompany } = useSelector(
    (state: any) => state.dashboardSearch
  );
  const { companyName, systemName, employee_cost_source } = useSelector(
    (state: any) => state.companyTabData
  );
  const selectedCompanyId = get(selectedCompany, "_id", "");
  const { deductionFileCategories } = useSelector(
    (state: any) => state.payrollSettingsTabData
  );
  const { role } = useSelector((state) => state.auth) as {
    role: string;
  };
  const {
    companyId: loadedCompanyId,
    systemPayrollCode,
    cashDCapitalId: storedCashDCapitalId,
    cashDFeeId: storedCashDFeeId,
    includeSubmitted,
    activePayroll,
    systemCode,
    apiKey,
    apiUserName,
    apiPassword,
    baseUrl,
    userName,
    userPassword,
    reckonAuthDetails,
    xeroAuthDetails,
    companyEmail,
    companyPhone,
    abn,
  } = useSelector((state: any) => state.companyTabData);

  const [localState, setLocalState] = useReducer(
    (prevState: any, newState: any) => ({ ...prevState, ...newState }),
    {
      cashDCapital: {},
      cashDFee: {},
    }
  );

  const { cashDCapital, cashDFee } = localState;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const data: any = event.data;
      if (!data || data.type !== "DEPUTY_AUTH_RESULT") return;

      if (data.success) {
        toast(data.message || "Deputy connected successfully.", {
          variant: "success",
        });

        // Best-effort refresh: some environments keep Deputy info in this slice.
        if (selectedCompanyId) {
          dispatch(fetchPayrollSettingsTabData(selectedCompanyId, systemPayrollCode));
        }
      } else {
        toast(data.message || "Failed to authenticate with Deputy.", {
          variant: "error",
        });
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [dispatch, selectedCompanyId, systemPayrollCode]);

  useEffect(() => {
    if (selectedCompanyId) {
      dispatch(fetchCompanyDetails(selectedCompanyId));
    }
  }, [dispatch, selectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId && loadedCompanyId === selectedCompanyId) {
      if (systemCode !== "XERO" && systemCode !== "DEPUTY" && systemCode !== "TIMESITE_PRO"  && activePayroll) {
        dispatch(
          fetchPayrollSettingsTabData(selectedCompanyId, systemPayrollCode)
        );
      }
    }
  }, [activePayroll, dispatch, selectedCompanyId, loadedCompanyId, systemCode, systemPayrollCode]);

  useEffect(() => {
    if (storedCashDCapitalId && storedCashDFeeId) {
      const getCategory = (id: string) =>
        deductionFileCategories.find((item: any) => item._id === id);

      setLocalState({
        cashDCapital: getCategory(storedCashDCapitalId),
        cashDFee: getCategory(storedCashDFeeId),
      });
    }
  }, [deductionFileCategories, storedCashDCapitalId, storedCashDFeeId]);

  const handleSyncMyob = useCallback(async () => {
    await dispatch(syncMYOBPayroll(selectedCompanyId));
  }, [dispatch, selectedCompanyId]);

  const handleSyncDeputy = useCallback(async () => {
    if (!selectedCompanyId) {
      toast("Please select a company first.", { variant: "error" });
      return;
    }

    try {
      const response = await authApi.get(`/api/timesheets/deputyreauth/${selectedCompanyId}`);
      const data = response.data;

      if (data.success && data.result?.url) {
        const popup = window.open(
          data.result.url,
          "deputyAuth",
          "width=1000,height=700,scrollbars=yes,resizable=yes"
        );
        if (!popup) {
          toast("Popup blocked. Please allow popups and try again.", {
            variant: "error",
          });
          return;
        }
        toast("Deputy authorization opened successfully.", { variant: "success" });
      } else {
        toast(data.message || "Failed to generate auth URL.", { variant: "error" });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to connect to Deputy.";
      toast(message, { variant: "error" });
    }
  }, [selectedCompanyId]);

  const handleSyncDeputyPayroll = useCallback(async () => {
    await dispatch(syncDeputyPayroll());
  }, [dispatch]);

  const handleSyncReckon = useCallback(async () => {
    if (!selectedCompanyId) {
      toast("Please select a company first.", { variant: "error" });
      return;
    }

    try {
      const response = await authApi.get("/v2/api/reckon/getOauthUrl?isReauth=true");
      const { oauthUrl } = response?.data?.data || {};

      if (oauthUrl) {
        const popup = window.open(
          oauthUrl,
          "reckonAuth",
          "resizable,scrollbars=0,status,top=100,left=200,width=800,height=400"
        );
        if (!popup) {
          toast("Popup blocked. Please allow popups and try again.", {
            variant: "error",
          });
          return;
        }
        popup?.focus();
        toast("Reckon authorization opened successfully.", { variant: "success" });

        const handleMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;

          const data = event.data;
          if (typeof data === "string" && data.startsWith("RECKONCODE=")) {
            const code = data.split("RECKONCODE=")[1];

            if (code === "done") {
              toast("Reckon connected successfully.", { variant: "success" });
              window.removeEventListener("message", handleMessage);
              dispatch(fetchPayrollSettingsTabData(selectedCompanyId, systemPayrollCode));
              return;
            }

            if (code === "error") {
              toast("Reckon authentication failed.", { variant: "error" });
              window.removeEventListener("message", handleMessage);
              return;
            }

            const accessCode =
              typeof code === "string" &&
              code.length > 10 &&
              code !== "done" &&
              code !== "error";

            if (!accessCode) return;

            try {
              const tokenResponse: any = await dispatch((processReckonAccessToken as any)(code, true));
              window.removeEventListener("message", handleMessage);

              if (tokenResponse) {
                const deductionMappings = {
                  capital: {
                    payItemId: storedCashDCapitalId || "",
                    payItemName: "",
                  },
                  fee: {
                    payItemId: storedCashDFeeId || "",
                    payItemName: "",
                  },
                };

                await dispatch(
                  (storeReckonCompanyDetails as any)({
                    tokenData: {
                      access_token: tokenResponse.accessToken,
                      refresh_token: tokenResponse.refreshToken,
                      expiresIn: tokenResponse.expiresIn,
                    },
                    deductionMappings,
                    companyId: selectedCompanyId,
                    isReauth: true,
                  })
                );

                toast("Reckon authentication updated successfully.", { variant: "success" });
                if (systemPayrollCode !== T.RECKON.toUpperCase()) {
                  dispatch(fetchPayrollSettingsTabData(selectedCompanyId, systemPayrollCode));
                }
              }
            } catch (err: any) {
              toast(err?.message || "Reckon authentication failed.", {
                variant: "error",
              });
              window.removeEventListener("message", handleMessage);
            }
          }
        };

        window.addEventListener("message", handleMessage);
      } else {
        toast("Failed to generate Reckon auth URL.", { variant: "error" });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to connect to Reckon.";
      toast(message, { variant: "error" });
    }
  }, [
    dispatch,
    selectedCompanyId,
    systemPayrollCode,
    storedCashDCapitalId,
    storedCashDFeeId,
  ]);
  
  const handleSync = useCallback(async () => {
    await dispatch(syncPayroll(selectedCompanyId));
  }, [dispatch, selectedCompanyId]);
  const PayrollComponent = useMemo(() => {
 
    // Check if we are in edit mode for TIMESITE_PRO systems
    if (systemCode === "TIMESITE_PRO" && pathname.includes('/edit')) {
      return (
        <EditPayrollSettingsTimeSitePro
          companyName={companyName}
          systemName={systemName}
          employee_cost_source={employee_cost_source}
          role={role}
        />
      );
    }

    // Check if we are viewing TIMESITE_PRO systems
    if (systemCode === "TIMESITE_PRO") {
      return (
        <ViewPayrollSettingsTimeSitePro
          companyName={companyName}
          systemName={systemName}
          employee_cost_source={employee_cost_source}
          role={role}
        />
      );
    }

    switch (systemPayrollCode) {
      case T.ASTUTE.toUpperCase():
        return (
          <ViewPayrollSettingsTimeSheet
            includeSubmitted={includeSubmitted}
            companyName={companyName}
            systemName={systemName}
            apiKey={apiKey}
            userName={apiUserName}
            password={apiPassword}
            role={role}
            handleSync={handleSync}
            />
        );
      case T.KEYPAY.toUpperCase():
        return (
          <ViewPayrollSettings
            cashDCapitalCategory={cashDCapital}
            cashDCapitalFeeCategory={cashDFee}
            companyName={companyName}
            systemName={systemName}
            role={role}
            handleSync={handleSync}
          />
        );
      case T.MYOBADVANCED.toUpperCase():
        return (
          <ViewPayrollSettingsMYOB
            baseURL={baseUrl}
            userName={userName}
            userPassword={userPassword}
            systemName={systemName}
            handleSync={handleSyncMyob}
          />
        );
      case "MYOBEXO":
      case T.MYOBEXO.toUpperCase():
        return (
          <ViewPayrollSettingsMYOBBusiness
            companyName={companyName}
            companyEmail={companyEmail}
            companyPhone={companyPhone}
            abn={abn}
            systemName={systemName}
            selectedCompanyId={selectedCompanyId}
            role={role}
            handleSyncMyob={handleSyncMyob}
          />
        );
      case T.DEPUTY.toUpperCase():
        return (
          <ViewPayrollSettingsBtn
            systemName={systemName}
            role={role}
            handleSyncDeputy={handleSyncDeputy}
            handleSyncDeputyPayroll={handleSyncDeputyPayroll}
          />
        );
      case T.RECKON.toUpperCase():
        return (
          <ViewPayrollSettingsReckon
            reckonAuthDetails={reckonAuthDetails}
            companyName={companyName}
            systemName={systemName}
            role={role}
            selectedCompanyId={selectedCompanyId}
            handleSyncReckon={handleSyncReckon}
          />
        );
      case "XERO":
      case T.XERO.toUpperCase():
        return (
          <ViewPayrollSettingsXero
            xeroAuthDetails={xeroAuthDetails}
            systemName={systemName}
            selectedCompanyId={selectedCompanyId}
            role={role}
            handleSyncXero={handleSync}
          />
        );
      default:
        return <MainCard title={systemName} />;
    }
  }, [
    systemPayrollCode,
    systemCode,
    pathname,
    includeSubmitted,
    companyName,
    systemName,
    employee_cost_source,
    apiKey,
    apiUserName,
    apiPassword,
    role,
    handleSync,
    handleSyncDeputy,
    handleSyncDeputyPayroll,
    handleSyncMyob,
    handleSyncReckon,
    cashDCapital,
    cashDFee,
    baseUrl,
    userName,
    userPassword,
    reckonAuthDetails,
    xeroAuthDetails,
    selectedCompanyId,
    companyEmail,
    companyPhone,
    abn,
  ]);

  return <>{PayrollComponent}</>;
};

export default ViewPayrollTab;
